import { Money } from '../components/ui/Money';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { Wallet, CheckCircle2, XCircle, Settings, Clock, ChevronRight, History, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending_manager: { label: 'Awaiting Manager', color: 'bg-amber-100 text-amber-700' },
  pending_hr: { label: 'Awaiting HR', color: 'bg-sky-100 text-sky-700' },
  pending_finance: { label: 'Awaiting Finance', color: 'bg-indigo-100 text-indigo-700' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-600' },
};

const moneyText = (n) => `AED ${Number(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money = (n) => <Money value={n} digits={2} />;
const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const SalaryAdvances = () => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('hr'); // hr | finance | all
  const [hrQueue, setHrQueue] = useState([]);
  const [finQueue, setFinQueue] = useState([]);
  const [allList, setAllList] = useState([]);
  const [overview, setOverview] = useState({ active: [], completed: [], total_outstanding: 0, total_repaid: 0 });
  const [settings, setSettings] = useState({ max_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [capInput, setCapInput] = useState('');
  const [commentFor, setCommentFor] = useState(null); // { id, level, action }
  const [comment, setComment] = useState('');
  const [detailOf, setDetailOf] = useState(null);

  const canManageSettings = ['super_admin', 'admin'].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hr, fin, all, st] = await Promise.all([
        axios.get(`${API}/api/salary-advances/admin/pending?level=hr`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/salary-advances/admin/pending?level=finance`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/salary-advances/admin/all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/salary-advances/admin/settings`, { headers }).catch(() => ({ data: { max_amount: 0 } })),
      ]);
      setHrQueue(hr.data || []);
      setFinQueue(fin.data || []);
      setAllList(all.data || []);
      setSettings(st.data || { max_amount: 0 });
      setCapInput(String((st.data && st.data.max_amount) || ''));
      axios.get(`${API}/api/advances/overview`, { headers })
        .then(r => setOverview(r.data || { active: [], completed: [] }))
        .catch(() => {});
    } catch (e) {
      // silent
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      const amt = parseFloat(capInput || '0') || 0;
      await axios.put(`${API}/api/salary-advances/admin/settings`, { max_amount: amt }, { headers });
      toast({ title: 'Settings saved' });
      setShowSettings(false);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const decide = async () => {
    if (!commentFor) return;
    try {
      const { id, level, action } = commentFor;
      await axios.post(
        `${API}/api/salary-advances/admin/${id}/action?level=${level}`,
        { action, comment }, { headers }
      );
      toast({ title: action === 'approve' ? `${level.toUpperCase()} approved` : 'Rejected' });
      setCommentFor(null); setComment('');
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const renderQueue = (rows, level) => (
    <div className="space-y-2">
      {rows.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-10 text-center text-gray-500">
          No requests awaiting {level.toUpperCase()}.
        </CardContent></Card>
      ) : rows.map(r => (
        <Card key={r.id} data-testid={`${level}-request-${r.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-[200px]">
                <p className="font-semibold">{r.employee_name} <span className="text-xs text-gray-400">({r.employee_code})</span></p>
                <p className="text-xs text-gray-500">{r.department}</p>
                <p className="text-[11px] text-gray-400">Submitted {fmt(r.created_at)}</p>
              </div>
              <div>
                <p className="text-xl font-bold text-emerald-700">{money(r.amount)}</p>
                {r.repayment_months && <p className="text-xs text-gray-500">Repayment: {r.repayment_months} month(s)</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDetailOf(r)} data-testid={`${level}-detail-${r.id}`}>
                  <History className="h-4 w-4 mr-1" /> Timeline
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => setCommentFor({ id: r.id, level, action: 'approve' })}
                  data-testid={`${level}-approve-${r.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-300"
                  onClick={() => setCommentFor({ id: r.id, level, action: 'reject' })}
                  data-testid={`${level}-reject-${r.id}`}>
                  <XCircle className="h-4 w-4 mr-1" /> Reject
                </Button>
              </div>
            </div>
            {r.reason && <p className="mt-3 text-sm text-gray-700 bg-gray-50 rounded-md px-3 py-2">{r.reason}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  );

  if (loading) return <div className="p-6 text-center text-gray-500" data-testid="salary-advances-loading">Loading…</div>;

  // Delete a request (admin) — also clears linked payroll advance
  const deleteRequest = async (req, e) => {
    if (e) e.stopPropagation();
    const ok = window.confirm(
      `Delete this salary advance request?\n\n` +
      `Employee: ${req.employee_name}\n` +
      `Amount: ${moneyText(req.amount)}\n` +
      `Status: ${req.status}\n\n` +
      `This will remove it from the employee portal too` +
      (req.payroll_advance_id ? ` AND remove the linked payroll advance entry.` : `.`)
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/salary-advances/admin/${req.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast({ title: 'Request deleted' });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4" data-testid="salary-advances-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Wallet className="h-7 w-7 text-emerald-600" />
            Salary Advance Requests
          </h1>
          <p className="text-sm text-gray-500">
            Multi-level approval: Manager → HR → Finance. Approved requests auto-add to the Payroll Advance section.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="settings-btn">
            <Settings className="h-4 w-4 mr-1" /> Cap: {settings.max_amount ? money(settings.max_amount) : 'None'}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button onClick={() => setTab('hr')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md flex items-center gap-1 ${tab === 'hr' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
          data-testid="tab-hr">
          <Clock className="h-4 w-4" />HR Queue
          {hrQueue.length > 0 && <span className="ml-1 bg-sky-500 text-white text-[10px] px-1.5 rounded-full">{hrQueue.length}</span>}
        </button>
        <button onClick={() => setTab('finance')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md flex items-center gap-1 ${tab === 'finance' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
          data-testid="tab-finance">
          <Clock className="h-4 w-4" />Finance Queue
          {finQueue.length > 0 && <span className="ml-1 bg-indigo-500 text-white text-[10px] px-1.5 rounded-full">{finQueue.length}</span>}
        </button>
        <button onClick={() => setTab('all')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md ${tab === 'all' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
          data-testid="tab-all">All</button>
        <button onClick={() => setTab('overview')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md flex items-center gap-1 ${tab === 'overview' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
          data-testid="tab-overview">
          <Wallet className="h-4 w-4" />Overview
        </button>
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="overview-panel">
          {/* Active advances */}
          <Card data-testid="overview-active">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Active Advances
                <Badge className="bg-amber-100 text-amber-700">{overview.active?.length || 0}</Badge>
                <span className="ml-auto text-sm font-normal text-red-600">Owed: {money(overview.total_outstanding)}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(!overview.active || overview.active.length === 0) ? (
                <p className="py-8 text-center text-sm text-gray-400">No staff with an outstanding advance.</p>
              ) : (
                <div className="divide-y">
                  {overview.active.map(r => (
                    <div key={r.employee_id} className="flex items-center justify-between px-4 py-2.5" data-testid={`overview-active-${r.employee_code}`}>
                      <div>
                        <p className="font-medium text-sm">{r.employee_name}</p>
                        <p className="text-[11px] text-gray-400">{r.employee_code} • {r.department}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-red-600">{money(r.outstanding)}</p>
                        <p className="text-[11px] text-gray-400">of {money(r.total_advanced)}{r.count > 1 ? ` · ${r.count} advances` : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed / paid off */}
          <Card data-testid="overview-completed">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Completed / Paid Off
                <Badge className="bg-emerald-100 text-emerald-700">{overview.completed?.length || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(!overview.completed || overview.completed.length === 0) ? (
                <p className="py-8 text-center text-sm text-gray-400">No fully repaid advances yet.</p>
              ) : (
                <div className="divide-y">
                  {overview.completed.map(r => (
                    <div key={r.employee_id} className="flex items-center justify-between px-4 py-2.5" data-testid={`overview-completed-${r.employee_code}`}>
                      <div>
                        <p className="font-medium text-sm">{r.employee_name}</p>
                        <p className="text-[11px] text-gray-400">{r.employee_code} • {r.department}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-600">{money(r.total_paid)}</p>
                        <p className="text-[11px] text-gray-400">repaid{r.count > 1 ? ` · ${r.count} advances` : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'hr' && renderQueue(hrQueue, 'hr')}
      {tab === 'finance' && renderQueue(finQueue, 'finance')}
      {tab === 'all' && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3">Employee</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Submitted</th>
                  <th className="text-left p-3">Payroll Advance</th>
                  <th className="text-right p-3 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allList.length === 0 ? (
                  <tr><td colSpan="6" className="p-6 text-center text-gray-500">No requests yet.</td></tr>
                ) : allList.map(r => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending_manager;
                  return (
                    <tr key={r.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setDetailOf(r)} data-testid={`all-row-${r.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{r.employee_name}</div>
                        <div className="text-[11px] text-gray-400">{r.employee_code} • {r.department}</div>
                      </td>
                      <td className="p-3 font-semibold">{money(r.amount)}</td>
                      <td className="p-3"><Badge className={meta.color}>{meta.label}</Badge></td>
                      <td className="p-3 text-xs">{fmt(r.created_at)}</td>
                      <td className="p-3 text-xs">{r.payroll_advance_id ? <span className="text-emerald-600">✓ Added</span> : '—'}</td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => deleteRequest(r, e)}
                          className="text-rose-600 hover:bg-rose-50"
                          title="Delete request (also removes from employee portal)"
                          data-testid={`delete-request-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader><DialogTitle>Salary Advance Settings</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Global max advance amount (AED)</Label>
              <Input type="number" min="0" step="0.01" value={capInput}
                onChange={e => setCapInput(e.target.value)}
                placeholder="0 = no cap" data-testid="settings-max-input" />
              <p className="text-[11px] text-gray-400 mt-1">Set 0 to allow any amount.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={saveSettings} disabled={!canManageSettings} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-save">
                Save
              </Button>
            </div>
            {!canManageSettings && <p className="text-xs text-red-500">Only Super Admin / Admin can change settings.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Comment dialog */}
      <Dialog open={!!commentFor} onOpenChange={() => { setCommentFor(null); setComment(''); }}>
        <DialogContent data-testid="comment-dialog">
          <DialogHeader>
            <DialogTitle>
              {commentFor?.action === 'approve' ? 'Approve' : 'Reject'} Request
              {commentFor ? ` (${commentFor.level.toUpperCase()})` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea rows={3} placeholder="Add a note (optional)" value={comment} onChange={e => setComment(e.target.value)} data-testid="admin-comment" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setCommentFor(null); setComment(''); }}>Cancel</Button>
              <Button onClick={decide}
                className={commentFor?.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="admin-confirm">
                Confirm {commentFor?.action}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timeline detail */}
      <Dialog open={!!detailOf} onOpenChange={() => setDetailOf(null)}>
        <DialogContent className="max-w-xl" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailOf?.employee_name} — {money(detailOf?.amount)}</DialogTitle>
          </DialogHeader>
          {detailOf && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className={(STATUS_META[detailOf.status] || STATUS_META.pending_manager).color}>
                  {(STATUS_META[detailOf.status] || {}).label || detailOf.status}
                </Badge>
                <span className="text-gray-500">Employee: {detailOf.employee_code}</span>
                <span className="text-gray-500">Dept: {detailOf.department}</span>
              </div>
              {detailOf.reason && <p className="text-sm bg-gray-50 rounded p-2">{detailOf.reason}</p>}
              <div className="border-t pt-2">
                <p className="text-xs font-semibold text-gray-500 mb-1">Timeline</p>
                {(detailOf.timeline || []).map((t, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs py-1">
                    <ChevronRight className="h-3 w-3 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <span className="capitalize font-medium">{t.stage}</span>
                      <span className="text-gray-500"> — {t.action} by {t.actor}</span>
                      {t.comment && <p className="text-gray-500 italic">"{t.comment}"</p>}
                    </div>
                    <span className="text-gray-400">{fmt(t.at)}</span>
                  </div>
                ))}
              </div>
              {detailOf.payroll_advance_id && (
                <p className="text-xs text-emerald-600 border-t pt-2">
                  ✓ Added to payroll advance section (id: {detailOf.payroll_advance_id.slice(0, 8)}…)
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryAdvances;
